import { create } from 'zustand';
import { CartService, CartItem, CartSummary, CartValidation } from '../services/cartService';

interface CartStore {
  items: CartItem[];
  summary: CartSummary | null;
  loading: boolean;
  error: string | null;
  validation: CartValidation | null;
  
  // Actions
  fetchCart: () => Promise<void>;
  addToCart: (supersedeNo: string, uom: string, quantity?: number) => Promise<void>;
  updateCartItem: (supersedeNoUOM: string, quantity: number) => Promise<void>;
  removeFromCart: (supersedeNoUOM: string) => Promise<void>;
  clearCart: () => Promise<void>;
  validateCart: () => Promise<void>;
  clearError: () => void;
  
  // Computed values
  getItemCount: () => number;
  getTotalQuantity: () => number;
  getSubtotal: () => number;
}

export const useCartStore = create<CartStore>((set, get) => ({
  items: [],
  summary: null,
  loading: false,
  error: null,
  validation: null,

  fetchCart: async () => {
    set({ loading: true, error: null });
    try {
      const response = await CartService.getCart();
      if (response.success) {
        set({
          items: response.data.items,
          summary: response.data.summary,
          validation: response.data.hasChanges ? {
            hasChanges: response.data.hasChanges,
            invalidItems: response.data.invalidItems || [],
            priceChanges: response.data.priceChanges || []
          } : null,
          loading: false
        });
      } else {
        set({ error: 'Failed to fetch cart', loading: false });
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch cart', 
        loading: false 
      });
    }
  },

  addToCart: async (supersedeNo: string, uom: string, quantity = 1) => {
    set({ loading: true, error: null });
    try {
      const response = await CartService.addToCart(supersedeNo, uom, quantity);
      if (response.success) {
        set({
          items: response.data.items,
          summary: response.data.summary,
          loading: false
        });
      } else {
        set({ error: 'Failed to add item to cart', loading: false });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to add item to cart', 
        loading: false 
      });
    }
  },

  updateCartItem: async (supersedeNoUOM: string, quantity: number) => {
    set({ loading: true, error: null });
    try {
      const response = await CartService.updateCartItem(supersedeNoUOM, quantity);
      if (response.success) {
        set({
          items: response.data.items,
          summary: response.data.summary,
          loading: false
        });
      } else {
        set({ error: 'Failed to update cart item', loading: false });
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update cart item', 
        loading: false 
      });
    }
  },

  removeFromCart: async (supersedeNoUOM: string) => {
    set({ loading: true, error: null });
    try {
      const response = await CartService.removeFromCart(supersedeNoUOM);
      if (response.success) {
        set({
          items: response.data.items,
          summary: response.data.summary,
          loading: false
        });
      } else {
        set({ error: 'Failed to remove item from cart', loading: false });
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to remove item from cart', 
        loading: false 
      });
    }
  },

  clearCart: async () => {
    set({ loading: true, error: null });
    try {
      const response = await CartService.clearCart();
      if (response.success) {
        set({
          items: [],
          summary: response.data.summary,
          loading: false
        });
      } else {
        set({ error: 'Failed to clear cart', loading: false });
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to clear cart', 
        loading: false 
      });
    }
  },

  validateCart: async () => {
    set({ loading: true, error: null });
    try {
      const response = await CartService.validateCart();
      if (response.success) {
        set({
          items: response.data.items,
          summary: response.data.summary,
          validation: response.data.validation || null,
          loading: false
        });
      } else {
        set({ error: 'Failed to validate cart', loading: false });
      }
    } catch (error) {
      console.error('Error validating cart:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to validate cart', 
        loading: false 
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  // Computed values
  getItemCount: () => {
    const { items } = get();
    return items.length;
  },

  getTotalQuantity: () => {
    const { items } = get();
    return items.reduce((total, item) => total + item.Qty, 0);
  },

  getSubtotal: () => {
    const { items } = get();
    return items.reduce((total, item) => total + item.TotalPrice, 0);
  },
}));